import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { ReviewCardsCompatibility } from '@/components/ReviewCards';
import { UiTypography } from '@/components/ui/Typography';
import { useAccount } from '@/hooks/useAccount';
import { useCreateStripeClient } from '@/hooks/useCreateStripeClient';
import { amplitudeModule } from '@/modules/amplitude';
import { apiV1GetMeAuthRequest } from '@/modules/api';
import { webCampaign } from '@/modules/webCampaign';
import { AnimationFill } from '@/pages/Onboarding/components/AnimationFill';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { userService } from '@/services/user';
import { trackOnboardingComplete } from '@/utils/analytics';
import { canLogEvents } from '@/utils/canLogEvents';
import { redirectToWebapp } from '@/utils/redirectToWebapp';

import { LayoutDataType } from '../../types';

import { ScreenSettingUpProfileStyles } from './SettingUpProfile.styles';

const ANIMATION_STEPS = [
  {
    delay: 0,
    label: 'Processing your request',
  },
  {
    delay: 4,
    label: 'Initializing AI modules',
  },
  {
    delay: 8,
    label: 'Adjusting your preferences',
  },
];

export const ScreenSettingUpProfile: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'SettingUpProfile',
      });
    }
  }, []);

  const form = useFormContext<LayoutDataType>();
  const { refreshUser } = useAccount();
  const formEmail = form.watch('email');

  const { createStripeClient, isCreatingStripeClient } =
    useCreateStripeClient();

  const [disabledNextStep, setDisabledNextStep] = React.useState<boolean>(true);

  const handleNextStep = async (): Promise<void> => {
    if (!formEmail) return;

    try {
      const lowerCaseEmail = formEmail.toLowerCase().trim();

      trackOnboardingComplete({ email: lowerCaseEmail });

      await Promise.all([
        createStripeClient(lowerCaseEmail),
        userService.createOrUpdate({
          onboarding_data: {
            onboarded: true,
          },
        }),
      ]);

      if (webCampaign.setup.redirectToWebappAfter === 'email') {
        const auth = await apiV1GetMeAuthRequest();

        if (auth?.token) {
          redirectToWebapp(auth.token);
          return;
        }
      }

      refreshUser();
    } catch (error) {
      console.error('Can not create a stripe user', error);
    }
  };

  const onFinishAnimation = () => {
    setDisabledNextStep(false);
  };

  return (
    <ScreenSettingUpProfileStyles background="dark-solid">
      <OwnAiTitle className="setting-up-title">
        Setting up your profile...
      </OwnAiTitle>
      <OnboardingSubtitle className="setting-up-subtitle">
        It will take less than one minute
      </OnboardingSubtitle>

      <IonContent>
        <AnimationFill
          onComplete={onFinishAnimation}
          show
          steps={ANIMATION_STEPS}
          stepDuration={4}
          titleToItemsSpacing={16}
          itemsSpacing={32}
          progressStyles={{
            backgroundColor: '#262130',
          }}
        />

        <section className="setting-up-user-reviews">
          <UiTypography variant="title-large" align="center" transform="bold">
            Trusted by 1,556,867 users
          </UiTypography>
          <ReviewCardsCompatibility />
        </section>
      </IonContent>

      <OnboardingFooter
        disabled={disabledNextStep || isCreatingStripeClient}
        onContinue={handleNextStep}
      />
    </ScreenSettingUpProfileStyles>
  );
};
